<template>
  <div class="creator-campaigns p-2">
    <h2 class="mb-2">Mis Campañas</h2>

    <div v-if="loading" class="d-flex justify-content-center my-4">
      <b-spinner variant="primary" />
    </div>

    <div v-else-if="error" class="text-center my-4 text-danger">
      {{ error }}
    </div>

    <div v-else-if="campaigns.length === 0" class="text-center my-4 text-muted">
      No tienes campañas asignadas aún.
    </div>

    <b-row v-else>
      <b-col
        v-for="item in campaigns"
        :key="item.uuid"
        cols="12"
        sm="6"
        xl="4"
        class="mb-2"
      >
        <b-card no-body class="campaign-card overflow-hidden">
          <!-- Cover -->
          <div class="campaign-card__cover position-relative">
            <b-img
              :src="coverUrl(item.campaign_influencer.campaign.cover)"
              class="w-100 campaign-card__cover-img"
              alt="Cover"
            />
            <!-- Active badge top-left -->
            <b-badge
              class="campaign-card__active-badge"
              :variant="item.campaign_influencer.campaign.active ? 'success' : 'secondary'"
            >
              {{ item.campaign_influencer.campaign.active ? 'Campaña activa' : 'Campaña inactiva' }}
            </b-badge>
          </div>

          <!-- Logo centered overlapping cover -->
          <div class="campaign-card__logo-wrap">
            <b-img
              :src="item.campaign_influencer.campaign.logo || require('@/assets/images/avatars/8.png')"
              rounded="circle"
              class="campaign-card__logo"
              alt="Logo"
            />
          </div>

          <b-card-body class="pt-4 pb-2 text-center">
            <!-- Campaign name -->
            <h5 class="campaign-card__name mb-1">{{ item.campaign_influencer.campaign.name }}</h5>

            <!-- Dates -->
            <small class="text-muted">
              {{ item.campaign_influencer.campaign.init_date }} — {{ item.campaign_influencer.campaign.end_date }}
            </small>

            <hr class="my-1" />

            <!-- Stats -->
            <b-row class="text-center mb-1 no-gutters">
              <b-col cols="4" class="px-1">
                <small class="text-muted d-block">Red</small>
                <span class="font-weight-bold text-capitalize">{{ item.user_network.network }}</span>
              </b-col>
              <b-col cols="4" class="px-1">
                <small class="text-muted d-block">Estado</small>
                <span class="font-weight-bold text-capitalize">{{ statusLabel(item.campaign_influencer.status) }}</span>
              </b-col>
              <b-col cols="4" class="px-1">
                <small class="text-muted d-block">Seguidores</small>
                <span class="font-weight-bold">{{ formatNumber(item.campaign_influencer.influencer.followers) }}</span>
              </b-col>
            </b-row>

            <!-- Toggle extra info -->
            <div class="text-center cursor-pointer mb-1" @click="toggleExpand(item.uuid)">
              <feather-icon
                :icon="expanded[item.uuid] ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                size="18"
              />
            </div>

            <!-- Código de campaña: visible siempre cuando está aprobado -->
            <div
              v-if="item.campaign_influencer.status_influencer === 'approved' && item.campaign_influencer.campaign.code_campaign"
              class="mb-1"
            >
              <small class="text-muted d-block">Código de campaña</small>
              <div class="d-flex align-items-center justify-content-center">
                <span class="font-weight-bold">{{ item.campaign_influencer.campaign.code_campaign }}</span>
                <feather-icon
                  :icon="copiedUuid === item.uuid ? 'CheckIcon' : 'CopyIcon'"
                  size="14"
                  class="ml-50 cursor-pointer"
                  :class="copiedUuid === item.uuid ? 'text-success' : 'text-primary'"
                  @click="copyExternalId(item)"
                />
              </div>
            </div>

            <b-collapse :visible="!!expanded[item.uuid]">
              <div class="mb-1">
                <small class="text-muted d-block">Precio colaboración</small>
                <span class="font-weight-bolder" style="font-size:1.2rem;">
                  $ {{ formatNumber(item.campaign_influencer.price_collab) }}
                </span>
              </div>
              <div class="mb-1">
                <small class="text-muted d-block">Estado de pago</small>
                <b-badge :variant="paymentVariant(item.campaign_influencer.status_link)">
                  {{ item.campaign_influencer.status_link }}
                </b-badge>
              </div>
            </b-collapse>

            <!-- Actions -->
            <b-row class="mt-1 no-gutters">
              <b-col>
                <b-button
                  block
                  variant="primary"
                  size="sm"
                  @click="viewInvitation(item)"
                >
                  Ver invitación
                </b-button>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <div v-if="count > campaigns.length" class="text-center mt-2">
      <b-button variant="outline-primary" size="sm" @click="loadMore" :disabled="loadingMore">
        <b-spinner v-if="loadingMore" small />
        Cargar más
      </b-button>
    </div>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardBody,
  BBadge,
  BButton,
  BImg,
  BSpinner,
  BCollapse,
} from 'bootstrap-vue'
import internal_campaigns from '@/services/internal_campaigns'
import config from '@/services/config'

export default {
  name: 'CreatorCampaigns',
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BBadge,
    BButton,
    BImg,
    BSpinner,
    BCollapse,
  },
  data() {
    return {
      campaigns: [],
      count: 0,
      page: 1,
      loading: false,
      loadingMore: false,
      error: null,
      expanded: {},
      copiedUuid: null,
    }
  },
  created() {
    this.fetchCampaigns()
  },
  methods: {
    async fetchCampaigns() {
      this.loading = true
      this.error = null
      try {
        const data = await internal_campaigns.getCampaignInfluencerUserNetworks(this.page)
        this.campaigns = data.results
        this.count = data.count
      } catch (e) {
        this.error = 'No se pudieron cargar las campañas.'
      } finally {
        this.loading = false
      }
    },
    async loadMore() {
      this.loadingMore = true
      try {
        this.page++
        const data = await internal_campaigns.getCampaignInfluencerUserNetworks(this.page)
        this.campaigns.push(...data.results)
      } catch (e) {
        this.page--
      } finally {
        this.loadingMore = false
      }
    },
    coverUrl(path) {
      if (!path) return require('@/assets/images/banner/banner.png')
      if (path.startsWith('http')) return path
      return `${config.urlCore}${path}`
    },
    toggleExpand(uuid) {
      this.$set(this.expanded, uuid, !this.expanded[uuid])
    },
    statusLabel(status) {
      const map = {
        approved: 'Aprobado',
        pending: 'Pendiente',
        rejected: 'Rechazado'
      }
      return map[status] || status
    },
    paymentVariant(status) {
      if (!status) return 'secondary'
      if (status.includes('pending')) return 'warning'
      if (status.includes('paid') || status.includes('approved')) return 'success'
      if (status.includes('rejected')) return 'danger'
      return 'secondary'
    },
    formatNumber(n) {
      if (!n && n !== 0) return '—'
      return Number(n).toLocaleString('es-MX')
    },
    copyExternalId(item) {
      const id = item.campaign_influencer.campaign.code_campaign
      navigator.clipboard.writeText(id).then(() => {
        this.copiedUuid = item.uuid
        setTimeout(() => { this.copiedUuid = null }, 2000)
      })
    },
    viewInvitation(item) {
      const slug = item.campaign_influencer.campaign.slug_name
      const network = item.campaign_influencer.influencer.network
      const username = item.campaign_influencer.influencer.username

      this.$router.push({
        name: 'new-detail-invitation-external',
        params: { slug, network, username }
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.campaign-card {
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
}

.campaign-card__cover {
  height: 140px;
  overflow: hidden;
  background: #e0e0e0;
}

.campaign-card__cover-img {
  width: 100%;
  height: 140px;
  object-fit: cover;
}

.campaign-card__active-badge {
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 0.78rem;
  padding: 0.3em 0.9em;
  border-radius: 20px;
}

.campaign-card__logo-wrap {
  display: flex;
  justify-content: center;
  margin-top: -44px;
  position: relative;
  z-index: 1;
}

.campaign-card__logo {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border: 3px solid #fff;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}

.campaign-card__name {
  font-weight: 700;
  font-size: 1rem;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
